import { Injectable } from '@angular/core';

import { BehaviorSubject, combineLatest, filter, map, Observable, pairwise, startWith, switchMap, tap } from 'rxjs';

import { Location } from '@shared/location/models';
import { AccountUsersService, LocationUsersService } from '../../../services';
import { AuthUser } from '@shared/signup/models';
import { AccountUser, LocationUser } from '@shared/user/models';
import { makeMap } from '@shared/core';
import { UserAccountRole } from '@shared/user/enums';
import { ILocationFilters } from './models';
import { LocationsFilterService } from './locations.filter.service';
import { AuthRestaurantService, LocationsService } from '@tablesready/frontend-store';

@Injectable()
export class LocationsListService {
  private user: AuthUser;

  constructor(
    private readonly authRestaurantService: AuthRestaurantService,
    private readonly locationsService: LocationsService,
    private readonly locationUsersService: LocationUsersService,
    private readonly accountUsersService: AccountUsersService,
    private readonly locationsFilterService: LocationsFilterService,
  ) {}

  selectLocations(filters: BehaviorSubject<ILocationFilters>): Observable<[Location[], Record<string, number>]> {
    const authUserObservable = this.authRestaurantService.select().pipe(
      startWith(null),
      pairwise(),
      filter(([prev, current]) => prev === null || prev.location?._id !== current.location?._id),
      tap(([, current])=> {
        this.user = current;
      }),
    );

    return combineLatest([
      combineLatest([
        this.locationsService.selectAll(),
        this.locationUsersService.selectAll(),
        this.accountUsersService.selectAll(),
        filters.pipe(
          startWith(null),
          pairwise(),
          filter(([prev, current]) => prev === null
            || current.info !== prev.info
            || current.planCode !== prev.planCode
            || current.availability !== prev.availability
          ),
          map(([, current]) => current)
        ),
      ]),
      // Reload data only if account has been changed
      authUserObservable.pipe(
        filter(([prev, current]) => current.location && (prev === null || prev.location._id !== current.location._id)),
        switchMap(() => combineLatest([
          this.locationsService.load(),
          this.locationUsersService.load(),
          this.accountUsersService.load(),
        ])),
      ),
    ])
      .pipe(
        filter(() => this.user.account !== undefined),
        map(([locationData]) => locationData),
        map(([locations, locationUsers, accountUsers, filters]) => {
          const indexOfCurrentLocation = locations.findIndex(location => location._id === this.user.location._id);
          const currentLocation = locations.splice(indexOfCurrentLocation, 1)[0];
          locations.unshift(currentLocation);

          return [
            this.locationsFilterService.filterLocations(filters, locations),
            this.createLocationUsersMap(locationUsers, accountUsers),
          ];
        }),
      );
  }

  private createLocationUsersMap(locationUsers: LocationUser[], accountUsers: AccountUser[]): Record<string, number> {
    const accountUsersMap = makeMap(accountUsers, 'userId');
    const locationUsersMap: Record<string, number> = {};

    for (let i = 0; i < locationUsers.length; i++) {
      const {userId, locationId} = locationUsers[i];
      if (accountUsersMap[userId]?.role === UserAccountRole.admin) {
        continue;
      }

      if (!locationUsersMap[locationId]) {
        locationUsersMap[locationId] = 1;
      } else {
        locationUsersMap[locationId]++
      }
    }

    return locationUsersMap;
  }
}
